import { ActionIcon, Divider, Loader, Text, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { X } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import DnDrawer from 'src/components/Core/DnDrawer'
import DnStepper from 'src/components/Core/DnStepper'
import { GET_APPLICANT_CUSTOM_FIELD_VALUE_AUDIT_TRAIL } from 'src/graphql/customFields.graphql'

interface ValueChangeHistoryDrawerProps {
  opened: boolean
  onClose: () => void
  applicantCustomFieldValueId: string | null
  fieldLabel: string | null
}

const ValueChangeHistoryDrawer = ({
  opened,
  onClose,
  applicantCustomFieldValueId,
  fieldLabel,
}: ValueChangeHistoryDrawerProps) => {
  const { data, loading } = useQuery(
    GET_APPLICANT_CUSTOM_FIELD_VALUE_AUDIT_TRAIL,
    {
      variables: { applicantCustomFieldValueId },
      skip: !applicantCustomFieldValueId || !opened,
    }
  )

  const auditTrail = data?.applicantCustomFieldValueAuditTrail || []

  const formatValue = (log) => {
    const fieldType = log.fieldType
    if (!fieldType) return { prevValue: null, newValue: null }

    let prevValue = null
    let newValue = null

    switch (fieldType) {
      case 'STRING':
        prevValue = log.prevStringValue
        newValue = log.newStringValue
        break
      case 'BOOLEAN':
        prevValue =
          log.prevBooleanValue !== null
            ? log.prevBooleanValue
              ? 'Yes'
              : 'No'
            : null
        newValue =
          log.newBooleanValue !== null
            ? log.newBooleanValue
              ? 'Yes'
              : 'No'
            : null
        break
      case 'DATE':
        prevValue = log.prevDateValue
          ? dayjs(log.prevDateValue).format('MM/DD/YYYY')
          : null
        newValue = log.newDateValue
          ? dayjs(log.newDateValue).format('MM/DD/YYYY')
          : null
        break
      case 'SINGLE_SELECT':
      case 'MULTI_SELECT':
        prevValue =
          log.prevSelectValues?.length > 0
            ? log.prevSelectValues.join(', ')
            : null
        newValue =
          log.newSelectValues?.length > 0
            ? log.newSelectValues.join(', ')
            : null
        break
    }

    return { prevValue, newValue }
  }

  const getEmployeeName = (employee) => {
    if (!employee) return 'System'
    return `${employee.firstName} ${employee.lastName}`
  }

  const getChangeTitle = (log) => {
    switch (log.changeType) {
      case 'CREATE':
        return `Created by ${getEmployeeName(log.employee)}`
      case 'UPDATE':
        return `Updated by ${getEmployeeName(log.employee)}`
      case 'DELETE':
        return `Deleted by ${getEmployeeName(log.employee)}`
      default:
        return `Changed by ${getEmployeeName(log.employee)}`
    }
  }

  const getChangeContent = (log) => {
    const { prevValue, newValue } = formatValue(log)

    if (log.changeType === 'CREATE') {
      return `Set to: ${newValue || '(empty)'}`
    }

    if (log.changeType === 'UPDATE') {
      return `From: ${prevValue || '(empty)'} → To: ${newValue || '(empty)'}`
    }

    if (log.changeType === 'DELETE') {
      return `Removed: ${prevValue || '(empty)'}`
    }

    return ''
  }

  // Sort audit trail by createdAt descending (most recent first)
  const sortedAuditTrail = [...auditTrail].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0
    return bTime - aTime
  })

  return (
    // Explicit zIndex instead of stackId: this drawer is opened from within the
    // applicant sidebar, which lives in Drawer.Stack with stackId="applicant-drawer"
    // (~201+). Without a zIndex this drawer defaults to 200 and paints behind the
    // sidebar, hiding it from the user. zIndex={210} renders it above the
    // stack-managed applicant drawer. Mirrors TaskModal/EmailSenderModal.
    <DnDrawer opened={opened} onClose={onClose} zIndex={210}>
      <div className="flex h-screen flex-col space-y-4">
        {/* HEADER */}
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            {fieldLabel && fieldLabel.length > 30 ? (
              <Tooltip label={fieldLabel} withArrow multiline maw={300}>
                <h2 className="truncate text-base font-semibold text-doubleNickel-gray-900">
                  {fieldLabel}
                </h2>
              </Tooltip>
            ) : (
              <h2 className="truncate text-base font-semibold text-doubleNickel-gray-900">
                {fieldLabel || 'Custom Field'}
              </h2>
            )}
          </div>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={onClose} className="flex-shrink-0">
            <X size={16} />
          </ActionIcon>
        </div>
        <Divider variant="dashed" />

        {/* BODY */}
        <div className="flex flex-1 flex-col space-y-4 overflow-auto pb-10">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader size="md" />
            </div>
          )}

          {!loading && (
            <DnStepper
              items={sortedAuditTrail.map((log) => ({
                id: log.auditTrailId,
                title: getChangeTitle(log),
                content: getChangeContent(log),
                createdAt: log.createdAt,
              }))}
              emptyMessage="No change history available for this field value."
            />
          )}
        </div>
      </div>
    </DnDrawer>
  )
}

export default ValueChangeHistoryDrawer
